import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import AuthLayout from './AuthLayout';

export default function Register() {
  const { register, verifyOTP, oauthLogin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [step, setStep] = useState<'info' | 'otp'>('info');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Dev-only: the API returns the OTP when Resend isn't configured so testers
  // can register without an email inbox. Removed automatically in production.
  const [devOtp, setDevOtp] = useState<string | null>(null);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const code = await register(email, name);
      if (code) {
        setDevOtp(code);
        setOtp(code.split(''));
      }
      setStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const next = document.getElementById(`reg-otp-${index + 1}`);
      next?.focus();
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prev = document.getElementById(`reg-otp-${index - 1}`);
      prev?.focus();
    }
  };

  // Distribute a pasted code across all boxes (handles "123456" copied as one
  // string). Digits are extracted so surrounding whitespace/hyphens are ignored.
  const handleOTPPaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!digits) return;
    const newOtp = [...otp];
    for (let i = 0; i < digits.length && index + i < 6; i++) {
      newOtp[index + i] = digits[i];
    }
    setOtp(newOtp);
    const lastFilled = Math.min(index + digits.length, 6) - 1;
    document.getElementById(`reg-otp-${lastFilled}`)?.focus();
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { setError(t('otp.errorLength')); return; }
    setLoading(true);
    setError(null);
    try {
      await verifyOTP(email, code);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      {step === 'info' ? (
        <form onSubmit={handleRegister}>
          <h2 className="text-xl font-bold text-text-main">{t('register.title')}</h2>
          <p className="mt-1 text-sm text-text-faint">{t('register.subtitle')}</p>
          {error && <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}
          <div className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-bold text-text-faint">{t('register.nameLabel')}</label>
              <input type="text" required value={name} onChange={e => setName(e.target.value)}
                className="w-full rounded-lg border border-border-subtle px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t('register.namePlaceholder')} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold text-text-faint">{t('register.emailLabel')}</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                className="w-full rounded-lg border border-border-subtle px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t('register.emailPlaceholder')} />
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary mt-6 w-full disabled:opacity-50">
            {loading ? t('register.sending') : t('register.submit')}
          </button>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 border-t border-border-subtle"></div>
            <span className="text-xs text-text-faint">{t('oauth.divider')}</span>
            <div className="flex-1 border-t border-border-subtle"></div>
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <button type="button" onClick={() => oauthLogin('google')}
              className="flex w-full items-center justify-center gap-3 rounded-lg border border-border-subtle px-4 py-2.5 text-sm font-bold text-text-main hover:bg-slate-50 transition-colors">
              <svg className="h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              {t('oauth.google')}
            </button>
            <button type="button" onClick={() => oauthLogin('github')}
              className="flex w-full items-center justify-center gap-3 rounded-lg border border-border-subtle px-4 py-2.5 text-sm font-bold text-text-main hover:bg-slate-50 transition-colors">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>
              {t('oauth.github')}
            </button>
          </div>

          <p className="mt-4 text-center text-sm text-text-faint">
            {t('register.haveAccount')} <Link to="/login" className="font-bold text-primary hover:underline">{t('register.signInLink')}</Link>
          </p>
        </form>
      ) : (
        <form onSubmit={handleVerifyOTP}>
          <h2 className="text-xl font-bold text-text-main">{t('otp.registerTitle')}</h2>
          <p className="mt-1 text-sm text-text-faint">
            <Trans i18nKey="auth:otp.subtitle" values={{ email }} components={{ 1: <span className="font-bold text-text-main" /> }} />
          </p>
          {error && <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}
          {devOtp && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              <span className="font-bold">Dev mode:</span> your code is{' '}
              <code className="font-mono font-bold tracking-widest">{devOtp}</code>
              <span className="block text-xs text-amber-600 mt-1">Email delivery is not configured — this is shown for local testing only.</span>
            </div>
          )}
          <div className="mt-6 flex justify-center gap-2">
            {otp.map((digit, i) => (
              <input key={i} id={`reg-otp-${i}`} type="text" inputMode="numeric" maxLength={1}
                value={digit} onChange={e => handleOTPChange(i, e.target.value)}
                onKeyDown={e => handleOTPKeyDown(i, e)}
                onPaste={e => handleOTPPaste(i, e)}
                className="h-12 w-12 rounded-lg border border-border-subtle text-center text-xl font-bold focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
            ))}
          </div>
          <button type="submit" disabled={loading} className="btn-primary mt-6 w-full disabled:opacity-50">
            {loading ? t('otp.verifying') : t('otp.verifyRegister')}
          </button>
          <button type="button" onClick={() => { setStep('info'); setOtp(['','','','','','']); setError(null); setDevOtp(null); }}
            className="mt-3 w-full rounded-lg border border-border-subtle px-4 py-2.5 text-sm font-bold text-text-faint hover:bg-slate-50">
            {t('otp.back')}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
